import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';
import type { PageType } from 'types';
import type { ProductListModule } from 'types/capi/article';
import type {
  Affiliate,
  Image as ImageType,
  ProductListItemExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import ExternalIcon from '~/assets/icons/ExternalIcon';
import AffiliatePageLink from '~/components/AffiliatePageLink';
import AffiliateProductLink from '~/components/AffiliateProductLink';
import Countdown from '~/components/Countdown';
import Image from '~/components/Image';
import Price from '~/components/Price';
import ProductCard from '~/components/ProductCard';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import Tracked from '~/components/Tracked';
import type { StockStatus as StockStatusType } from '~/domain/stockStatus';
import { getStockStatus, textColours } from '~/domain/stockStatus';
import useUser from '~/hooks/useUser';
import { formatReleaseDateLong } from '~/utils/dates';
import getHexBrightness from '~/utils/hexBrightness';

type CatalogProduct = ProductListItemExternal | ProductListItemInternal;

export default function ProductList(
  props: ProductListModule & { pageType: PageType }
) {
  switch (props.display_type) {
    case 'carousel':
      return <ProductListCarousel {...props} pageType={props.pageType} />;
    case 'grid':
      return <ProductListGrid {...props} pageType={props.pageType} />;
    case 'block':
      return (
        <ProductListBlock
          backgroundImage={props.background_image}
          backgroundColor={props.background_colour}
          product={props.items[0]!}
          pageType={props.pageType}
        />
      );
    default:
      return null;
  }
}

function useListImageRatio(items: ProductListModule['items']) {
  return items.some((item) => item.image?.ratio === '4:5')
    ? ('4:5' as const)
    : undefined;
}

function ProductListCarousel({
  header,
  description,
  items,
  background_colour = '#FAFAFA',
  pageType
}: {
  background_colour?: string;
  description?: string;
  header?: string;
  items: ProductListModule['items'];
  pageType: PageType;
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;
  const listRatio = useListImageRatio(items);

  return (
    <div
      className={`col-span-full w-full px-4 md:px-10 ${
        background_colour === '#FAFAFA' ? 'py-8' : 'py-12 max-md:py-10'
      } ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div className="mx-auto flex h-fit max-w-desktop flex-col items-center gap-8">
        {(header || description) && (
          <div className="flex w-full flex-col items-center gap-2 text-center">
            {header && <div className="text-title-lg">{header}</div>}
            {description && <div className="text-sm">{description}</div>}
          </div>
        )}
        <div
          className={`custom-scrollbar flex w-full overflow-x-scroll ${
            shouldCentreItems ? 'md:justify-center md:overflow-x-visible' : ''
          } md:px-0`}
        >
          <div className="flex shrink-0 gap-2 md:gap-6">
            {items.map((product) => {
              const className =
                'h-auto w-[calc(50vw-24px)] min-w-[140px] max-w-[360px] shrink-0 md:w-[285px] md:min-w-[285px]';

              if (product.type === 'inline-external') {
                return (
                  <ProductCard.InlineExternal
                    cardSize="small"
                    name={`PRODUCT_CARD_${product.name}`}
                    className={className}
                    imageLoading="eager"
                    listRatio={listRatio}
                    key={product.url}
                    padImage
                    pagePosition="Product Carousel"
                    product={product}
                  />
                );
              }

              return product.type === 'external' ? (
                <ProductCard.External
                  allowNotifications
                  cardSize="small"
                  name={`PRODUCT_CARD_${product.name}`}
                  className={className}
                  imageLoading="eager"
                  listRatio={listRatio}
                  key={product.id}
                  openPdp={false}
                  padImage
                  pagePosition="Product Carousel"
                  product={product}
                  pageType={pageType}
                />
              ) : (
                <ProductCard.Internal
                  allowNotifications
                  cardSize="small"
                  name={`PRODUCT_CARD_${header}`}
                  className={className}
                  imageLoading="eager"
                  listRatio={listRatio}
                  key={product.id}
                  padImage
                  pageType={pageType}
                  pagePosition="Product Carousel"
                  product={product}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductListGrid({
  header,
  description,
  items,
  background_colour = '#2A2528',
  pageType
}: {
  background_colour?: string;
  description?: string;
  header?: string;
  items: ProductListModule['items'];
  pageType: PageType;
}) {
  const bgBrightness = getHexBrightness(background_colour);
  const shouldCentreItems = items.length < 4;
  const listRatio = useListImageRatio(items);

  const columnClass = shouldCentreItems
    ? `md:grid-cols-${items.length}`
    : 'lg:grid-cols-4';

  return (
    <div
      className={`w-full px-4 md:px-10 ${
        background_colour === '#FAFAFA' ? 'py-8' : 'py-12 max-md:py-10'
      } ${bgBrightness === 'dark' ? 'text-white' : 'text-black'}`}
      style={{ backgroundColor: background_colour }}
    >
      <div className="mx-auto flex max-w-desktop flex-col items-center gap-6">
        {(header || description) && (
          <div className="flex flex-col items-center gap-2 text-center">
            {header && <h2 className="text-title-lg">{header}</h2>}
            {description && (
              <p
                className={`text-sm ${bgBrightness === 'dark' ? 'text-gray-300' : 'text-black'}`}
              >
                {description}
              </p>
            )}
          </div>
        )}
        <div
          className={`w-full ${shouldCentreItems ? 'flex justify-center' : ''}`}
        >
          <div
            className={`grid grid-cols-2 ${columnClass} gap-x-2 gap-y-4 bg-inherit md:gap-6`}
          >
            {items.map((product) => {
              const className = 'mx-auto h-auto shrink-0';

              switch (product.type) {
                case 'internal':
                  return (
                    <ProductCard.Internal
                      allowNotifications
                      cardSize="small"
                      name={`PRODUCT_CARD_${header}`}
                      className={className}
                      imageLoading="eager"
                      listRatio={listRatio}
                      key={product.id}
                      padImage
                      pageType={pageType}
                      pagePosition="Product Grid"
                      product={product}
                    />
                  );
                case 'external':
                  return (
                    <ProductCard.External
                      allowNotifications
                      cardSize="small"
                      name={`PRODUCT_CARD_${product.name}`}
                      className={className}
                      imageLoading="eager"
                      listRatio={listRatio}
                      key={product.id}
                      openPdp={false}
                      padImage
                      pagePosition="Product Grid"
                      product={product}
                      pageType={pageType}
                    />
                  );
                case 'inline-external':
                  return (
                    <ProductCard.InlineExternal
                      cardSize="small"
                      name={`PRODUCT_CARD_${product.name}`}
                      className={className}
                      imageLoading="eager"
                      listRatio={listRatio}
                      key={product.url}
                      padImage
                      pagePosition="Product Grid"
                      product={product}
                    />
                  );
                default:
                  return <></>;
              }
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductListBlock({
  backgroundImage,
  backgroundColor,
  product,
  pageType
}: {
  backgroundColor?: string;
  backgroundImage?: ImageType;
  pageType: PageType;
  product: ProductListModule['items'][0];
}) {
  const isInlineExternal = product.type === 'inline-external';

  return (
    <Backdrop
      image={backgroundImage}
      backgroundColor={backgroundColor}
      productImage={product.image}
    >
      <div className={`flex w-full max-w-[552px] flex-col gap-5`}>
        {isInlineExternal ? (
          <div className="flex flex-col gap-6 rounded-lg bg-white p-6">
            <div className="flex flex-col gap-2">
              <h3 className="text-title-lg">{product.name}</h3>
              <span className="text-base tracking-[-0.16px] text-grey-read">
                <Price
                  regular={product.price.regular}
                  sale={product.price.sale}
                  currency={product.price.currency}
                />
              </span>
            </div>

            {product.affiliate && (
              <AffiliatePageLink
                affiliate={product.affiliate}
                urlTemplates={product}
                buttonName="PRODUCT_CARD"
                pagePosition="Featured Product Block"
                variant="forest"
                className="flex h-12 items-center justify-center gap-2"
              >
                <ExternalIcon color="#FFFFFF" size={16} />
                Buy now from {product.affiliate.name}
              </AffiliatePageLink>
            )}
          </div>
        ) : (
          <>
            <span className="text-title-lg text-white">{product.name}</span>
            <div className="rounded-lg bg-white px-3 py-4">
              {['in-stock', 'reseller'].includes(product.stock_status) ? (
                <InStockProductInfo product={product} />
              ) : (
                <OutOfStockProductInfo product={product} />
              )}
            </div>
          </>
        )}
      </div>

      <div className="w-full md:max-w-[360px]">
        {isInlineExternal ? (
          <ProductCard.InlineExternal
            cardSize="large"
            padImage
            pagePosition="Featured Product Block"
            product={product}
            withMetadata={false}
          />
        ) : product.type === 'external' ? (
          <ProductCard.External
            allowNotifications
            cardSize="large"
            padImage
            openPdp={false}
            pagePosition="Featured Product Block"
            product={product}
            pageType={pageType}
            withMetadata={false}
          />
        ) : (
          <ProductCard.Internal
            allowNotifications
            cardSize="large"
            name="PRODUCT_CARD"
            padImage
            pageType={pageType}
            pagePosition="Featured Product Block"
            product={product}
            withMetadata={false}
          />
        )}
      </div>
    </Backdrop>
  );
}

function Backdrop({
  children,
  image,
  backgroundColor,
  productImage
}: {
  backgroundColor?: string;
  children: React.ReactNode;
  image?: ImageType;
  productImage?: ImageType;
}) {
  return (
    <div className="relative z-10 w-full overflow-hidden px-4 py-10 md:px-10">
      {image ? (
        <Image
          loading="eager"
          className="absolute inset-0 -z-40 block size-full object-cover"
          alt={image.alt}
          src={image.url}
        />
      ) : productImage ? (
        <img
          loading="eager"
          className="absolute inset-0 -z-40 block size-full scale-[160%] object-cover blur-lg"
          alt=""
          src={productImage.url}
        />
      ) : (
        <div
          className="absolute inset-0 -z-40 block size-full"
          style={{ backgroundColor: backgroundColor ?? '#000000' }}
        />
      )}
      <div className="absolute inset-0 -z-30 bg-black opacity-40"></div>
      <div className="mx-auto flex max-w-desktop flex-col items-center justify-between gap-6 md:flex-row">
        {children}
      </div>
    </div>
  );
}

function InStockProductInfo({ product }: { product: CatalogProduct }) {
  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white p-1">
      <div className="mb-2 flex w-full flex-row items-center justify-between">
        <div className="flex items-center gap-1">
          <AffiliateLogo affiliate={product.affiliate} />
          <StockStatus status={product.stock_status} />
        </div>
        <Price {...product.price} className="flex gap-1" />
      </div>
      <InStockSizes product={product} />
      {product.type === 'external' ? (
        <AffiliateProductLink
          className="flex h-12 items-center justify-center"
          affiliate={product.affiliate}
          pagePosition="Featured Product Block"
          buttonPosition={-1}
          price={product.price}
          product={{
            name: product.name,
            price: product.price,
            product_id: product.id
          }}
          urlTemplates={{
            click_ref_template: product.click_ref_template,
            url: product.url,
            url_template: product.url_template
          }}
          variant="forest"
        >
          Buy now from {product.affiliate.name}
        </AffiliateProductLink>
      ) : (
        <Tracked.InternalLink
          to={product.path}
          variant="forest"
          name="PRODUCT_CARD"
          pagePosition="Featured Product Block"
        >
          Buy now from {product.affiliate.name}
        </Tracked.InternalLink>
      )}
    </div>
  );
}

function OutOfStockProductInfo({ product }: { product: CatalogProduct }) {
  const isNotified = useUser().isNotificationSetForProduct(product.legacy_id);
  const stockStatus = getStockStatus(product.stock_status);

  const isUpcoming =
    stockStatus === 'coming-soon' || stockStatus === 'upcoming';

  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white p-1">
      <div className="flex items-center justify-between">
        <div className="flex flex-col items-start">
          <StockStatus status={product.stock_status} />
          {isUpcoming && product.date.released && (
            <div className="text-sm font-light text-grey-read">
              {formatReleaseDateLong(product.date.released)}
            </div>
          )}
        </div>
        {isUpcoming && product.date.released && (
          <ClientOnly>
            {() => <Countdown toUnixTimestamp={product.date.released!} />}
          </ClientOnly>
        )}
      </div>
      <div className="flex w-full flex-col gap-2">
        <ProductNotificationButton
          product={{
            affiliateName:
              product.type === 'external' ? product.affiliate.name : undefined,
            id: product.id,
            image: product.image,
            legacyId: product.legacy_id,
            name: product.name,
            price: product.price.regular
          }}
          className="h-12 w-full"
          pagePosition="Dynamic Product Block"
          buttonStyle="normal"
        />
        <NotificationTooltip
          stockStatus={getStockStatus(product.stock_status)}
          isNotified={isNotified}
        />
      </div>
    </div>
  );
}

function AffiliateLogo({ affiliate }: { affiliate: Affiliate }) {
  return (
    <Image
      className="w-14"
      src={affiliate.logo}
      loading="lazy"
      alt={`${affiliate.name}-logo`}
      responsiveOptions={{
        xs: { width: 150 }
      }}
    />
  );
}

const InStockSizes = ({ product }: { product: CatalogProduct }) => {
  if (!product.sizes?.length) return null;

  return (
    <ul className="flex	flex-wrap items-center gap-1">
      <li className="mr-2 text-xs tracking-wide text-grey-hide">
        UK sizes in stock:
      </li>
      {product.sizes.map((size) =>
        product.type === 'external' ? (
          <AffiliateProductLink
            key={`sl-size-${size}`}
            affiliate={product.affiliate}
            price={product.price}
            product={product}
            buttonPosition={-1}
            pagePosition="Featured Product Block"
            urlTemplates={{
              click_ref_template: product.click_ref_template,
              url: product.url,
              url_template: product.url_template
            }}
            variant="none"
          >
            <Size>{size}</Size>
          </AffiliateProductLink>
        ) : (
          <Tracked.InternalLink
            key={`sl-size-${size}`}
            to={product.path}
            variant="none"
            name="PRODUCT_CARD"
            pagePosition="Featured Product Block"
          >
            <Size>{size}</Size>
          </Tracked.InternalLink>
        )
      )}
    </ul>
  );
};

function Size({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex h-5 items-center rounded-xl  border border-grey-divide bg-white p-2 text-xs text-grey-hide">
      {children}
    </li>
  );
}

function NotificationTooltip({
  isNotified,
  stockStatus
}: {
  isNotified: boolean;
  stockStatus?: StockStatusType;
}) {
  if (isNotified) return null;

  return (
    <div className="translate-y-[-120%] animate-slide-from-top text-clip">
      <div className="bg-purple-hola-lm p-2 text-center text-xs text-purple-hola">
        {stockStatus === 'sold-out'
          ? 'Get a restock alert'
          : 'Get a release alert'}
      </div>
    </div>
  );
}

const StockStatus = ({ status }: { status: string }) => {
  const { t } = useTranslation();
  const stockStatus = getStockStatus(status);

  if (!stockStatus) return null;

  return (
    <div className={`text-base tracking-wide ${textColours[stockStatus]}`}>
      {t(stockStatus)}
    </div>
  );
};
